// WhatsappOptInCheckbox — the D-16 explicit WhatsApp consent row shown in both OTP steps
// (login PhoneAuth and claim ClaimOtpStep), under the code input and above the submit button.
//
// Fully controlled: the parent owns the value and MUST initialise it to false, because a
// pre-checked box would weaken the stored consent as evidence (UI-SPEC assumption 1). The parent
// sends the value as `whatsappOptIn` with its verify call and captures `whatsapp_optin_changed`
// only after that verify succeeds, so this component has no analytics.
//
// The same rule covers variant "onboarding" (Phase 49 S2 finish screen, a bordered row with a
// Switch): the parent MUST initialise that switch to false too (FINISH_WHATSAPP_INITIAL), since
// only an explicit turn-on recorded on submit counts as the D-13 first-week consent.
//
// All copy comes from WHATSAPP_OPTIN_COPY (shared), so this file holds no Arabic literals.

import React, { useId } from "react";
import { WHATSAPP_OPTIN_COPY } from "@shared/notificationCopy";
import { MessageCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

type WhatsappOptInCheckboxProps = {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  variant: "login" | "claim" | "onboarding";
  // Claim and onboarding: the number, already masked (maskPhone). Never the raw number.
  phoneMasked?: string;
  disabled?: boolean;
};

const font = "var(--ys-font)";

// Split marker so the masked number can sit in its own dir="ltr" span inside the RTL sentence.
const SLOT = "\u0000";

function ClaimHelper({ phoneMasked }: { phoneMasked: string }) {
  const [before, after = ""] = WHATSAPP_OPTIN_COPY.helperClaim(SLOT).split(SLOT);
  return (
    <>
      {before}
      <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
        {phoneMasked}
      </span>
      {after}
    </>
  );
}

function OnboardingFootnote({ phoneMasked }: { phoneMasked: string }) {
  const [before, after = ""] = WHATSAPP_OPTIN_COPY.onboardingFootnote(SLOT).split(SLOT);
  return (
    <>
      {before}
      <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
        {phoneMasked}
      </span>
      {after}
    </>
  );
}

// S2 item 5: the whole row is a <label> for the switch; ON tints the row with the accent.
function OnboardingRow({
  checked,
  onCheckedChange,
  phoneMasked,
  disabled,
}: Omit<WhatsappOptInCheckboxProps, "variant">) {
  const id = useId();
  const valueId = `${id}-value`;
  const footnoteId = `${id}-footnote`;

  return (
    <label
      htmlFor={id}
      dir="rtl"
      style={{
        fontFamily: font,
        textAlign: "start",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: 16,
        minHeight: 44,
        borderRadius: "var(--ys-r)",
        border: `1px solid ${checked ? "var(--ys-primary)" : "var(--ys-rule)"}`,
        background: checked ? "var(--ys-primary-soft)" : "var(--ys-paper)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <MessageCircle
        aria-hidden="true"
        size={20}
        style={{ flexShrink: 0, color: checked ? "var(--ys-primary)" : "var(--ys-ink-2)" }}
      />
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: "var(--ys-ink)" }}>
          {WHATSAPP_OPTIN_COPY.onboardingTitle}
        </span>
        <span id={valueId} style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.6, color: "var(--ys-ink-2)" }}>
          {WHATSAPP_OPTIN_COPY.onboardingValue}
        </span>
        {phoneMasked && (
          <span id={footnoteId} style={{ fontSize: 12, fontWeight: 400, lineHeight: 1.5, color: "var(--ys-ink-3)" }}>
            <OnboardingFootnote phoneMasked={phoneMasked} />
          </span>
        )}
      </span>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={disabled}
        aria-describedby={phoneMasked ? `${valueId} ${footnoteId}` : valueId}
        className="shrink-0 data-[state=checked]:bg-[color:var(--ys-primary)]"
      />
    </label>
  );
}

export default function WhatsappOptInCheckbox({
  checked,
  onCheckedChange,
  variant,
  phoneMasked,
  disabled,
}: WhatsappOptInCheckboxProps) {
  const id = useId();
  const helperId = `${id}-helper`;

  if (variant === "onboarding") {
    return (
      <OnboardingRow
        checked={checked}
        onCheckedChange={onCheckedChange}
        phoneMasked={phoneMasked}
        disabled={disabled}
      />
    );
  }

  return (
    <div dir="rtl" style={{ fontFamily: font, textAlign: "start" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          disabled={disabled}
          aria-describedby={helperId}
          className="h-5 w-5 border-[color:var(--ys-rule-2)] data-[state=checked]:border-[color:var(--ys-primary)] data-[state=checked]:bg-[color:var(--ys-primary)] data-[state=checked]:text-white"
        />
        <Label
          htmlFor={id}
          style={{
            fontFamily: font,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ys-ink)",
            cursor: disabled ? "not-allowed" : "pointer",
            flex: 1,
            minHeight: 44,
            display: "flex",
            alignItems: "center",
          }}
        >
          {WHATSAPP_OPTIN_COPY.checkboxLabel}
        </Label>
      </div>
      <p
        id={helperId}
        style={{
          fontFamily: font,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.5,
          color: "var(--ys-ink-3)",
          margin: 0,
        }}
      >
        {variant === "login" ? (
          WHATSAPP_OPTIN_COPY.helperLogin
        ) : (
          // The login helper says "this same number", which is wrong for a claim, so a claim
          // without a masked number shows no number rather than the login line.
          phoneMasked && <ClaimHelper phoneMasked={phoneMasked} />
        )}
      </p>
    </div>
  );
}
